import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Settings, Eye, ArrowRight, ArrowLeft, MapPin } from 'lucide-react';
import { UserHeader } from '../components/UserHeader';
import { goBack } from '../utils/navigation';

export default function RecurseroMenuPage() {
  const navigate = useNavigate();
  const { isAuthenticated, loading, trabajador } = useAuth();

  // Verificación de autenticación
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      // Guardar redirect para volver al recursero después del login
      sessionStorage.setItem('redirectAfterLogin', '/recursero');
      navigate('/login', { replace: true });
    }
  }, [loading, isAuthenticated, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header unificado */}
      <UserHeader />
      
      {/* Subtítulo de sección */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-6 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Recursero Colaborativo</h1>
          <p className="text-gray-500 max-w-xl mx-auto">
            Guía de Recursos del Colegio Profesional de Trabajadorxs Sociales de Lomas de Zamora
          </p>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-10">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">
            ¿Qué querés hacer?
          </h2>
          <p className="text-gray-500">
            Elegí una opción para continuar
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Buscar Cercanos */}
          <Card 
            className="hover:shadow-xl transition-all cursor-pointer border-2 hover:border-orange-500 group"
            onClick={() => navigate('/recursero/mapa')}
            data-testid="buscar-cercanos-card"
          >
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-orange-100 rounded-full flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                <MapPin className="w-10 h-10 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Buscar Cercanos
              </h3>
              <p className="text-gray-500 mb-6 text-sm">
                Encontrá instituciones y recursos cerca de tu ubicación actual con mapa interactivo.
              </p>
              <Button className="w-full bg-orange-600 hover:bg-orange-700">
                Abrir mapa
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          {/* Gestionar Recursero */}
          <Card 
            className="hover:shadow-xl transition-all cursor-pointer border-2 hover:border-emerald-500 group"
            onClick={() => navigate('/trabajador/gestionar-recursero')}
            data-testid="gestionar-recursero-card"
          >
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-emerald-100 rounded-full flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                <Settings className="w-10 h-10 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Gestionar Recursero
              </h3>
              <p className="text-gray-500 mb-6 text-sm">
                Gestioná tu visibilidad en el recursero y actualizá tus datos laborales.
              </p>
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700">
                Gestionar perfil
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          {/* Ver Recurseros */}
          <Card 
            className="hover:shadow-xl transition-all cursor-pointer border-2 hover:border-blue-500 group"
            onClick={() => navigate('/recursero/seleccionar')}
            data-testid="ver-recurseros-card"
          >
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                <Eye className="w-10 h-10 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Ver Recurseros
              </h3>
              <p className="text-gray-500 mb-6 text-sm">
                Consultá el recursero de colegas y la guía institucional colaborativa.
              </p>
              <Button className="w-full">
                Ver recurseros
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Botón volver */}
        <div className="text-center mt-8">
          <Button 
            variant="outline" 
            onClick={() => goBack()}
            data-testid="back-to-services-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
        </div>
      </main>
    </div>
  );
}
